import { useEffect, useState } from 'react';
import PulsanteSegnala from '../components/PulsanteSegnala';
import RisposteSegnalazioni from '../components/RisposteSegnalazioni';
import { salApi } from '../api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import OfferteConsulentePanel from '../components/OfferteConsulentePanel';
import SchedaSal from '../components/SchedaSal';
import SelettoreCommessa from '../components/SelettoreCommessa';
import CambiaPassword from '../components/CambiaPassword';
import CaricaDaExcel from '../components/CaricaDaExcel';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const STATO = {
  bozza:     { l: 'Bozza — da inviare', c: 'bg-gray-100 text-gray-600' },
  inviato:   { l: 'In attesa di approvazione', c: 'bg-blue-100 text-blue-700' },
  approvato: { l: 'Approvato', c: 'bg-green-100 text-green-700' },
  rifiutato: { l: 'Rifiutato', c: 'bg-red-100 text-red-600' },
};

export default function PortaleEsterno() {
  const { user, logout } = useAuth();
  const [cambioPassword, setCambioPassword] = useState(false);
  const [commesse, setCommesse] = useState([]);
  const [sal, setSal] = useState([]);
  const [filtroCommessa, setFiltroCommessa] = useState('');
  const [nuovo, setNuovo] = useState(null);     // form di creazione
  const [apertoId, setApertoId] = useState(null); // SAL in lavorazione
  const [salvando, setSalvando] = useState(false);

  const load = () => salApi.list(filtroCommessa ? { commessa_id: filtroCommessa } : {}).then(setSal);
  useEffect(() => { salApi.commesseAccessibili().then(setCommesse).catch(() => setCommesse([])); }, []);
  useEffect(() => { load(); }, [filtroCommessa]);

  const commessaDi = id => commesse.find(c => String(c.id) === String(id));
  const apertoSal = sal.find(s => s.id === apertoId);
  const commessaAperta = apertoSal ? commessaDi(apertoSal.commessa_id) : null;

  const creaSal = async () => {
    if (!nuovo.commessa_id) return alert('Scegli la commessa');
    if (!nuovo.descrizione.trim()) return alert('Descrivi il periodo o l\'attività');
    setSalvando(true);
    try {
      const creato = await salApi.create(nuovo);
      setNuovo(null);
      await load();
      setApertoId(creato.id);   // si apre subito per inserire le righe
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const invia = async (r) => {
    if (!confirm('Inviare il rendiconto per approvazione? Dopo l\'invio potrai ancora modificarlo finché non viene approvato.')) return;
    try { await salApi.invia(r.id); await load(); } catch (e) { alert(e); }
  };

  const elimina = async (r) => {
    if (!confirm('Eliminare questo rendiconto e tutte le sue righe?')) return;
    try { await salApi.delete(r.id); setApertoId(null); await load(); } catch (e) { alert(e); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-brand-700 text-white px-6 py-4 flex items-center justify-between shadow">
        <div>
          <div className="font-bold text-lg tracking-tight">SINTHEMA — Portale Collaboratori</div>
          <div className="text-brand-200 text-xs mt-0.5">Benvenuto, <span className="font-semibold">{user?.nome_completo || user?.nome}</span></div>
        </div>
        {/* Anche i collaboratori segnalano: sono quelli che incontrano i
            problemi sul campo, ed è lì che vanno raccolti. */}
        <div className="flex items-center gap-4 shrink-0">
          <div className="w-44"><PulsanteSegnala /></div>
          <button onClick={() => setCambioPassword(true)}
                  className="text-brand-200 hover:text-white text-sm underline whitespace-nowrap">Cambia password</button>
          <button onClick={logout} className="text-brand-200 hover:text-white text-sm underline">Esci</button>
          {cambioPassword && <CambiaPassword onClose={() => setCambioPassword(false)} />}
        </div>
      </header>

      <RisposteSegnalazioni />

      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Commesse assegnate */}
        <section>
          <h2 className="text-base font-semibold text-gray-700 mb-3">Le tue commesse</h2>
          {commesse.length === 0
            ? <div className="card text-center text-gray-400 py-6 text-sm">Nessuna commessa assegnata. Contatta l'amministratore.</div>
            : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {commesse.map(c => (
                  <div key={c.id} className="card border-l-4 border-brand-500">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{c.numero} — {c.titolo}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{c.cliente_nome}</div>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                        c.modalita_esecuzione === 'economia' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                        {c.modalita_esecuzione === 'economia' ? 'in economia' : 'a corpo'}
                      </span>
                    </div>
                    {c.mie_tariffe && (
                      <div className="text-[11px] text-gray-500 mt-2 border-t pt-2">
                        Le tue tariffe · Front {c.mie_tariffe.front_office_giorno ? `${fmt(c.mie_tariffe.front_office_giorno)}/gg` : '—'}
                        {' · '}Back {c.mie_tariffe.back_office_giorno ? `${fmt(c.mie_tariffe.back_office_giorno)}/gg` : '—'}
                        {c.mie_tariffe.km ? ` · ${String(c.mie_tariffe.km).replace('.', ',')} €/km` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
          }
        </section>

        <OfferteConsulentePanel />

        {/* Rendiconti / SAL */}
        <section>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-gray-700">I tuoi rendiconti</h2>
            <div className="flex gap-2 items-center">
              {/* Il cliente prima di tutto: il numero di commessa è nostro e a
                  chi collabora con noi non dice niente. */}
              <select className="input py-1 text-sm" value={filtroCommessa} onChange={e => setFiltroCommessa(e.target.value)}>
                <option value="">Tutte le commesse</option>
                {commesse.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.cliente_nome ? `${c.cliente_nome} — ` : ''}{c.titolo} ({c.numero})
                  </option>
                ))}
              </select>
              <button className="btn-primary btn-sm" disabled={commesse.length === 0}
                      onClick={() => setNuovo({ commessa_id: '', descrizione: '', data_sal: new Date().toISOString().slice(0, 10), percentuale: '' })}>
                + Nuovo rendiconto
              </button>
            </div>
          </div>

          {/* Chi tiene il proprio foglio Excel non deve ricopiarlo riga per
              riga: lo carica, controlla l'anteprima e i rendiconti nascono. */}
          {commesse.length > 0 && (
            <div className="mb-3">
              <CaricaDaExcel onCreati={load} />
            </div>
          )}

          <div className="space-y-2">
            {sal.length === 0 && <div className="card text-center text-gray-400 py-6 text-sm">Nessun rendiconto</div>}
            {sal.map(s => {
              const st = STATO[s.stato] || STATO.bozza;
              const modificabile = ['bozza', 'inviato', 'rifiutato'].includes(s.stato);
              return (
                <div key={s.id} className="card">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.c}`}>{st.l}</span>
                        {s.n_rettifiche > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                                title="Gli importi sono stati corretti dopo l'approvazione: apri il rendiconto per il dettaglio">
                            rettificato
                          </span>
                        )}
                        <span className="text-xs text-gray-700">{s.cliente_nome}</span>
                        <span className="text-xs text-gray-400">{s.commessa_numero}</span>
                        <span className="text-xs text-gray-400">{fmtDate(s.data_sal)}</span>
                      </div>
                      <div className="font-medium text-gray-800 mt-1">{s.descrizione || '(senza descrizione)'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {s.modalita_esecuzione === 'economia' ? (
                          <>
                            {Number(s.tot_giorni) > 0 && `${Number(s.tot_giorni)} giornate`}
                            {Number(s.tot_giorni) > 0 && Number(s.tot_ore) > 0 && ' · '}
                            {Number(s.tot_ore) > 0 && `${Number(s.tot_ore)} ore`}
                            {(Number(s.tot_giorni) > 0 || Number(s.tot_ore) > 0) && ' · '}
                          </>
                        ) : (s.percentuale ? `avanzamento ${Number(s.percentuale)}% · ` : '')}
                        {Number(s.tot_spese) > 0 && `spese ${fmt(s.tot_spese)} · `}
                        <span className="text-gray-700 font-medium">tuo credito {fmt(s.importo_costo)}</span>
                      </div>
                      {s.note_admin && (
                        <div className={`text-xs mt-1.5 px-2 py-1 rounded ${s.stato === 'rifiutato' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                          💬 {s.note_admin}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button className="btn-secondary btn-sm" onClick={() => setApertoId(apertoId === s.id ? null : s.id)}>
                        {apertoId === s.id ? 'Chiudi' : 'Compila'}
                      </button>
                      {modificabile && s.stato !== 'inviato' && (
                        <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                                onClick={() => invia(s)}>📤 Invia</button>
                      )}
                      {modificabile && (
                        <button className="btn-danger btn-sm" onClick={() => elimina(s)}>Elimina</button>
                      )}
                    </div>
                  </div>

                  {apertoId === s.id && (
                    <div className="mt-4 pt-4 border-t">
                      {/* Descrizione, data e avanzamento stanno nella scheda qui
                          sotto. Erano ripetuti anche qui: due campi per lo stesso
                          dato, con etichette diverse, facevano credere che
                          l'avanzamento andasse dichiarato due volte. */}
                      <SchedaSal
                        salId={s.id}
                        commessa={commessaDi(s.commessa_id)}
                        soloLettura={!modificabile}
                        mostraRicavo={false}
                        onCambiato={load}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Creazione nuovo rendiconto */}
      {nuovo && (
        <Modal title="Nuovo rendiconto" onClose={() => setNuovo(null)} size="md">
          <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3">
            📋 Crea il rendiconto, poi inserisci attività e spese. Resta in bozza finché non lo invii.
          </div>
          <div className="space-y-3">
            <div>
              <label className="label">
                Commessa *
                <span className="ml-1 text-xs text-gray-400 font-normal">(cercabile per cliente)</span>
              </label>
              {/* Elenco cercabile con il cliente in evidenza: lo stesso che
                  usiamo internamente. Con il solo numero di commessa bisognava
                  andarsi a cercare di chi si trattasse. */}
              <SelettoreCommessa
                commesse={commesse}
                value={nuovo.commessa_id}
                onChange={v => setNuovo(n => ({ ...n, commessa_id: v }))}
                placeholder="— Scegli la commessa —"
              />
              {(() => {
                const c = commessaDi(nuovo.commessa_id);
                if (!c) return null;
                return (
                  <p className="text-xs text-gray-500 mt-1.5">
                    {c.modalita_esecuzione === 'economia'
                      ? 'In economia: rendiconterai giornate, ore ed eventuali spese.'
                      : 'A corpo: indicherai quanto avanza il lavoro con questo rendiconto.'}
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="label">Descrizione periodo / attività *</label>
              <input className="input" placeholder="es. Attività di giugno 2026"
                     value={nuovo.descrizione} onChange={e => setNuovo(n => ({ ...n, descrizione: e.target.value }))} />
            </div>
            <div>
              <label className="label">Data</label>
              <input className="input" type="date" value={nuovo.data_sal}
                     onChange={e => setNuovo(n => ({ ...n, data_sal: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setNuovo(null)}>Annulla</button>
            <button className="btn-primary" onClick={creaSal} disabled={salvando}>{salvando ? 'Creo…' : 'Crea e compila'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
